import { useState, useEffect } from 'react';
import type { MenuItem, MenuVariant, MenuAddon } from '@/lib/data';
import type { CartItem } from '../page';

interface ItemDetailModalProps {
  item: MenuItem;
  cartItem: CartItem | undefined;
  onClose: () => void;
  onAddToCart: (cartItem: CartItem) => void;
  /** Plan gate: when true, the Add-to-cart action is disabled (browse-only). */
  browseOnly?: boolean;
}

const foodTypeConfig: Record<string, { icon: string; color: string; bg: string }> = {
  veg: { icon: 'ri-leaf-fill', color: 'text-green-600', bg: 'bg-green-100' },
  'non-veg': { icon: 'ri-circle-fill', color: 'text-red-500', bg: 'bg-red-100' },
  egg: { icon: 'ri-circle-fill', color: 'text-amber-500', bg: 'bg-amber-100' },
};

export default function ItemDetailModal({ item, cartItem, onClose, onAddToCart, browseOnly = false }: ItemDetailModalProps) {
  const [selectedVariant, setSelectedVariant] = useState<MenuVariant | null>(null);
  const [selectedAddons, setSelectedAddons] = useState<MenuAddon[]>([]);
  const [quantity, setQuantity] = useState(1);
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (cartItem) {
      setSelectedVariant(cartItem.selectedVariant);
      setSelectedAddons(cartItem.selectedAddons);
      setQuantity(cartItem.quantity);
      setSpecialInstructions(cartItem.specialInstructions || '');
    } else {
      setSelectedVariant(item.variants.length > 0 ? item.variants[0] : null);
      setSelectedAddons([]);
      setQuantity(1);
      setSpecialInstructions('');
    }
    requestAnimationFrame(() => setVisible(true));
  }, [item, cartItem]);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 200);
  };

  const toggleAddon = (addon: MenuAddon) => {
    setSelectedAddons((prev) =>
      prev.find((a) => a.name === addon.name)
        ? prev.filter((a) => a.name !== addon.name)
        : [...prev, addon],
    );
  };

  const unitPrice = selectedVariant ? selectedVariant.price : item.price;
  const addonsTotal = selectedAddons.reduce((sum, a) => sum + a.price, 0);
  const itemTotal = (unitPrice + addonsTotal) * quantity;

  const handleAdd = () => {
    onAddToCart({
      menuItem: item,
      selectedVariant,
      selectedAddons,
      quantity,
      specialInstructions,
    });
    handleClose();
  };

  const ft = foodTypeConfig[item.foodType];

  return (
    <div
      className={`fixed inset-0 z-40 flex items-end justify-center transition-opacity duration-200 ${
        visible ? 'opacity-100' : 'opacity-0'
      }`}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={handleClose} />

      {/* Sheet */}
      <div
        className={`relative w-full max-w-md bg-background-50 rounded-t-2xl max-h-[90vh] overflow-hidden flex flex-col transition-transform duration-200 ${
          visible ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-2.5 pb-1">
          <div className="w-10 h-1 bg-background-300 rounded-full" />
        </div>

        {/* Header */}
        <div className="px-5 pb-3 flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className={`w-5 h-5 ${ft.bg} rounded-full flex items-center justify-center`}>
                <i className={`${ft.icon} ${ft.color} text-[8px]`}></i>
              </span>
              <h2 className="text-lg font-semibold text-foreground-950 font-heading truncate">{item.name}</h2>
            </div>
            <p className="text-sm text-foreground-500 leading-relaxed">{item.description}</p>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 hover:bg-background-200 flex-shrink-0 ml-2 cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-foreground-600"></i>
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-5 pb-5 space-y-5">
          {/* Image */}
          {item.image && (
            <div className="w-full h-44 rounded-xl overflow-hidden">
              <img src={item.image} alt={item.name} className="w-full h-full object-cover object-center" />
            </div>
          )}

          {/* Variants */}
          {item.variants.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-foreground-400 uppercase tracking-wide mb-2.5 font-label">Choose Size / Portion</h3>
              <div className="space-y-1.5">
                {item.variants.map((v) => (
                  <button
                    key={v.name}
                    onClick={() => setSelectedVariant(v)}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 transition-all cursor-pointer ${
                      selectedVariant?.name === v.name
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-background-200 hover:border-background-300 bg-background-50'
                    }`}
                  >
                    <span className="text-sm font-medium text-foreground-800">{v.name}</span>
                    <span className="text-sm font-bold text-foreground-950">₹{v.price}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Addons */}
          {item.addons.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-foreground-400 uppercase tracking-wide mb-2.5 font-label">Add-ons & Customizations</h3>
              <div className="space-y-1.5">
                {item.addons.map((ad) => (
                  <button
                    key={ad.name}
                    onClick={() => toggleAddon(ad)}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 transition-all cursor-pointer ${
                      selectedAddons.find((a) => a.name === ad.name)
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-background-200 hover:border-background-300 bg-background-50'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className={`w-4 h-4 rounded-full flex items-center justify-center ${
                          item.foodType === 'veg' ? 'border border-green-500' : 'border border-red-500'
                        }`}
                      >
                        <span className={`w-2 h-2 rounded-full ${item.foodType === 'veg' ? 'bg-green-500' : 'bg-red-500'}`} />
                      </span>
                      <span className="text-sm font-medium text-foreground-800">{ad.name}</span>
                    </div>
                    <span className="text-sm font-semibold text-foreground-700">+ ₹{ad.price}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Special instructions */}
          <div>
            <h3 className="text-xs font-semibold text-foreground-400 uppercase tracking-wide mb-2 font-label">Special Instructions</h3>
            <textarea
              value={specialInstructions}
              onChange={(e) => setSpecialInstructions(e.target.value)}
              maxLength={200}
              placeholder="Any preferences? e.g. less spicy, no onion..."
              className="w-full px-4 py-3 bg-background-100 border border-background-200 rounded-xl text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 resize-none h-20 transition-all"
            />
            <p className="text-[10px] text-foreground-400 mt-1 text-right">{specialInstructions.length}/200</p>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-background-200 px-5 py-4 bg-background-50">
          <div className="flex items-center gap-4">
            {/* Quantity stepper */}
            <div className="flex items-center gap-0 bg-background-100 rounded-xl border border-background-200">
              <button
                onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                disabled={quantity <= 1}
                className="w-10 h-10 flex items-center justify-center text-foreground-600 disabled:text-foreground-300 cursor-pointer disabled:cursor-not-allowed transition-colors"
              >
                <i className="ri-subtract-line"></i>
              </button>
              <span className="w-10 text-center text-sm font-bold text-foreground-950 font-heading">{quantity}</span>
              <button
                onClick={() => setQuantity((q) => q + 1)}
                className="w-10 h-10 flex items-center justify-center text-primary-500 cursor-pointer transition-colors"
              >
                <i className="ri-add-line"></i>
              </button>
            </div>

            {/* Add button — disabled in browse-only (plan below Growth) */}
            {browseOnly ? (
              <button
                disabled
                className="flex-1 py-3 bg-background-200 text-foreground-400 font-semibold text-sm rounded-xl cursor-not-allowed whitespace-nowrap"
              >
                Online ordering not available
              </button>
            ) : (
              <button
                onClick={handleAdd}
                className="flex-1 py-3 bg-accent-500 hover:bg-accent-600 text-background-50 font-semibold text-sm rounded-xl transition-all active:scale-[0.98] cursor-pointer whitespace-nowrap"
              >
                {cartItem ? 'Update Item' : 'Add to Cart'} — ₹{itemTotal}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
